import { CalendarDays, Edit, Search, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS, buildApiUrl } from '../config/api.js';
import { useAuth } from '../contexts/AuthContext';

const SEARCH_DEBOUNCE_MS = 300;

const LANGUAGE_OPTIONS = [
  { value: 'es', label: 'Español' },
  { value: 'en', label: 'Inglés' },
  { value: 'de', label: 'Alemán' },
];

const Clientes = () => {
  const { user, authenticatedFetch } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
  const [editingCliente, setEditingCliente] = useState(null);
  const [formData, setFormData] = useState({});
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  /** Cliente que no se puede eliminar por tener reservas/pagos asociados (modal informativo) */
  const [blockedDeleteCliente, setBlockedDeleteCliente] = useState(null);

  // Búsqueda en servidor con debounce; vuelve a la primera página
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPagination(prev => ({ ...prev, page: 1 }));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchClientes = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({ page: pagination.page, limit: pagination.limit });
      if (debouncedSearch) params.set('search', debouncedSearch);

      const response = await authenticatedFetch(`${buildApiUrl(API_ENDPOINTS.CUSTOMERS)}?${params}`);
      const data = await response.json();

      if (data.success) {
        setClientes(data.data.customers);
        setPagination(prev => ({
          ...prev,
          total: data.data.pagination.total,
          totalPages: data.data.pagination.totalPages
        }));
      } else {
        setError(data.message || 'Error al cargar los clientes');
      }
    } catch (err) {
      console.error('Error al cargar clientes:', err);
      setError('Error al cargar los clientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recargar solo al cambiar página, tamaño o búsqueda
  }, [pagination.page, pagination.limit, debouncedSearch]);

  const verReservas = (cliente) => {
    navigate('/reservas', {
      state: {
        customerFilter: { id: cliente.id, fullName: cliente.fullName, email: cliente.email }
      }
    });
  };

  const handleEdit = (cliente) => {
    setEditingCliente(cliente);
    setFormData({
      fullName: cliente.fullName || '',
      email: cliente.email || '',
      phonePrefix: cliente.phonePrefix || '',
      phone: cliente.phone || '',
      dni: cliente.dni || '',
      language: cliente.language || 'es'
    });
    setFormError(null);
  };

  const closeForm = () => {
    setEditingCliente(null);
    setFormError(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setFormError(null);
      const response = await authenticatedFetch(`${buildApiUrl(API_ENDPOINTS.CUSTOMERS)}/${editingCliente.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (!data.success) {
        setFormError(data.message || 'Error al guardar el cliente');
        return;
      }
      closeForm();
      fetchClientes();
    } catch (err) {
      console.error('Error al guardar cliente:', err);
      setFormError('Error al guardar el cliente');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cliente) => {
    if (cliente.bookingsCount > 0) {
      setBlockedDeleteCliente(cliente);
      return;
    }
    if (!confirm(`¿Seguro que quieres eliminar al cliente "${cliente.fullName}"? Esta acción no se puede deshacer.`)) {
      return;
    }
    try {
      const response = await authenticatedFetch(`${buildApiUrl(API_ENDPOINTS.CUSTOMERS)}/${cliente.id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      // Puede tener pagos sin reserva o una reserva creada tras cargar la tabla
      if (data.error === 'CUSTOMER_HAS_BOOKINGS') {
        setBlockedDeleteCliente(cliente);
        fetchClientes();
        return;
      }
      if (!data.success) {
        alert(data.message || 'Error al eliminar el cliente');
        return;
      }
      // Si era el último de la página, retroceder una página
      if (clientes.length === 1 && pagination.page > 1) {
        setPagination(prev => ({ ...prev, page: prev.page - 1 }));
      } else {
        fetchClientes();
      }
    } catch (err) {
      console.error('Error al eliminar cliente:', err);
      alert('Error al eliminar el cliente');
    }
  };

  const formatPhone = (cliente) => {
    if (!cliente.phone) return '—';
    // Algunos teléfonos ya se guardaron con el prefijo incluido
    if (!cliente.phonePrefix || cliente.phone.trim().startsWith('+')) return cliente.phone;
    return `${cliente.phonePrefix} ${cliente.phone}`;
  };

  const formatDate = (value) => (value ? new Date(value).toLocaleDateString('es-ES') : '—');

  return (
    <div className="content-page">
      <div className="page-header">
        <h1>Clientes registrados</h1>
      </div>

      <div className="table-controls">
        <div className="search-container">
          <Search size={16} />
          <input
            type="text"
            placeholder="Buscar por nombre, email, teléfono o DNI..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="pagination-controls">
          <label>
            Mostrar:
            <select
              value={pagination.limit}
              onChange={(e) => setPagination(prev => ({ ...prev, limit: Number(e.target.value), page: 1 }))}
              className="items-per-page-select"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </label>
          <span>{pagination.total} clientes</span>
        </div>
      </div>

      <div className="table-container">
        {error ? (
          <div className="no-users">
            <p>{error}</p>
            <button className="btn-primary" onClick={fetchClientes}>Reintentar</button>
          </div>
        ) : loading && clientes.length === 0 ? (
          <div className="no-users"><p>Cargando clientes…</p></div>
        ) : clientes.length === 0 ? (
          <div className="no-users">
            <p>{debouncedSearch ? `No se encontraron clientes que coincidan con "${debouncedSearch}"` : 'No hay clientes registrados'}</p>
          </div>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Teléfono</th>
                  <th>DNI</th>
                  <th>Idioma</th>
                  <th>Reservas</th>
                  <th>Última reserva</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {clientes.map((cliente) => (
                  <tr key={cliente.id}>
                    <td>#{cliente.id}</td>
                    <td>{cliente.fullName}</td>
                    <td>{cliente.email}</td>
                    <td>{formatPhone(cliente)}</td>
                    <td>{cliente.dni || '—'}</td>
                    <td>{(cliente.language || 'es').toUpperCase()}</td>
                    <td>{cliente.bookingsCount}</td>
                    <td>{formatDate(cliente.lastBookingDate)}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon"
                          title="Ver reservas del cliente"
                          onClick={() => verReservas(cliente)}
                          disabled={cliente.bookingsCount === 0}
                        >
                          <CalendarDays size={16} />
                        </button>
                        {isAdmin && (
                          <>
                            <button
                              className="btn-icon"
                              title="Editar cliente"
                              onClick={() => handleEdit(cliente)}
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              className="btn-icon btn-danger"
                              title="Eliminar cliente"
                              onClick={() => handleDelete(cliente)}
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {pagination.totalPages > 1 && (
              <div className="pagination">
                <button
                  className="btn-secondary"
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(prev.page - 1, 1) }))}
                  disabled={pagination.page === 1 || loading}
                >
                  Anterior
                </button>
                <span>Página {pagination.page} de {pagination.totalPages}</span>
                <button
                  className="btn-secondary"
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.page + 1, prev.totalPages) }))}
                  disabled={pagination.page === pagination.totalPages || loading}
                >
                  Siguiente
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {blockedDeleteCliente && (
        <div className="form-overlay">
          <div className="form-container">
            <div className="form-header">
              <h2>No se puede eliminar el cliente</h2>
              <button className="btn-close" onClick={() => setBlockedDeleteCliente(null)}>×</button>
            </div>
            <p>
              El cliente <strong>{blockedDeleteCliente.fullName}</strong> ({blockedDeleteCliente.email}) tiene{' '}
              {blockedDeleteCliente.bookingsCount > 0
                ? <><strong>{blockedDeleteCliente.bookingsCount}</strong> {blockedDeleteCliente.bookingsCount === 1 ? 'reserva asociada' : 'reservas asociadas'}</>
                : 'reservas o pagos asociados'}
              . Solo se pueden eliminar clientes sin reservas ni pagos.
            </p>
            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => setBlockedDeleteCliente(null)}>
                Cerrar
              </button>
              {blockedDeleteCliente.bookingsCount > 0 && (
                <button type="button" className="btn-primary" onClick={() => verReservas(blockedDeleteCliente)}>
                  Ver reservas
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {editingCliente && (
        <div className="form-overlay">
          <div className="form-container">
            <div className="form-header">
              <h2>Editar cliente #{editingCliente.id}</h2>
              <button className="btn-close" onClick={closeForm}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Nombre *</label>
                  <input type="text" name="fullName" value={formData.fullName} onChange={handleInputChange} required />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Prefijo</label>
                  <input type="text" name="phonePrefix" value={formData.phonePrefix} onChange={handleInputChange} placeholder="+34" />
                </div>
                <div className="form-group">
                  <label>Teléfono *</label>
                  <input type="text" name="phone" value={formData.phone} onChange={handleInputChange} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>DNI / Pasaporte</label>
                  <input type="text" name="dni" value={formData.dni} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Idioma</label>
                  <select name="language" value={formData.language} onChange={handleInputChange}>
                    {LANGUAGE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {formError && <p style={{ color: '#dc3545' }}>{formError}</p>}

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={closeForm} disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Guardando…' : 'Guardar cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clientes;
